/**
 * Game Service
 * Handles all game-related API calls
 */

import apiClient from '../client';
import { API_ENDPOINTS } from '../config';
import {
  GameSession,
  GameStats,
  GameSessionsParams,
  PaginatedResponse,
  VersionStats,
} from '../types';

class GameService {
  /**
   * Get all game sessions
   */
  async getGameSessions(params?: GameSessionsParams): Promise<PaginatedResponse<GameSession>> {
    const response = await apiClient.get<PaginatedResponse<GameSession>>(
      API_ENDPOINTS.GAMES.SESSIONS,
      { params }
    );
    return response.data;
  }

  /**
   * Get game session by ID
   */
  async getGameSessionById(id: string): Promise<GameSession> {
    const response = await apiClient.get<GameSession>(
      API_ENDPOINTS.GAMES.BY_ID(id)
    );
    return response.data;
  }

  /**
   * Get game statistics
   */
  async getGameStats(params?: {
    dateFrom?: string;
    dateTo?: string;
    gameId?: string;
  }): Promise<GameStats> {
    const response = await apiClient.get<GameStats>(
      API_ENDPOINTS.GAMES.STATS,
      { params }
    );
    return response.data;
  }

  /**
   * Get opened games statistics
   */
  async getOpenedGamesStats(): Promise<{
    stats: VersionStats[];
    totals: {
      last24h: number;
      lastWeek: number;
      total: number;
    };
  }> {
    const response = await apiClient.get<{
      stats: VersionStats[];
      totals: {
        last24h: number;
        lastWeek: number;
        total: number;
      };
    }>(API_ENDPOINTS.GAMES.OPENED);
    return response.data;
  }

  /**
   * Get game levels data
   */
  async getGameLevels(gameId?: string): Promise<Array<{
    levelId: string;
    levelName: string;
    completionRate: number;
    averageScore: number;
    playCount: number;
  }>> {
    const response = await apiClient.get<Array<{
      levelId: string;
      levelName: string;
      completionRate: number;
      averageScore: number;
      playCount: number;
    }>>(API_ENDPOINTS.GAMES.LEVELS, {
      params: { gameId },
    });
    return response.data;
  }

  /**
   * Get chart data for games
   */
  async getGameChartData(
    chartType: 'bar' | 'line',
    gameId?: string
  ): Promise<Array<{ name: string; [key: string]: string | number }>> {
    const response = await apiClient.get<Array<{ name: string; [key: string]: string | number }>>(
      `${API_ENDPOINTS.GAMES.STATS}/chart`,
      {
        params: {
          chartType,
          gameId,
        },
      }
    );
    return response.data;
  }

  /**
   * Get top players for a game
   */
  async getTopPlayers(
    gameId: string,
    params?: {
      limit?: number;
      dateFrom?: string;
      dateTo?: string;
    }
  ): Promise<Array<{
    userId: string;
    username: string;
    highScore: number;
    totalPlays: number;
    rank: number;
  }>> {
    const response = await apiClient.get<Array<{
      userId: string;
      username: string;
      highScore: number;
      totalPlays: number;
      rank: number;
    }>>(`${API_ENDPOINTS.GAMES.BY_ID(gameId)}/top-players`, { params });
    return response.data;
  }

  /**
   * Get game session trends
   */
  async getSessionTrends(params?: {
    gameId?: string;
    period?: 'day' | 'week' | 'month';
  }): Promise<Array<{
    date: string;
    sessionCount: number;
    uniquePlayers: number;
    averageDuration: number;
  }>> {
    const response = await apiClient.get<Array<{
      date: string;
      sessionCount: number;
      uniquePlayers: number;
      averageDuration: number;
    }>>(`${API_ENDPOINTS.GAMES.STATS}/trends`, { params });
    return response.data;
  }

  /**
   * Create game session
   */
  async createGameSession(data: Partial<GameSession>): Promise<GameSession> {
    const response = await apiClient.post<GameSession>(
      API_ENDPOINTS.GAMES.SESSIONS,
      data
    );
    return response.data;
  }

  /**
   * Update game session
   */
  async updateGameSession(id: string, data: Partial<GameSession>): Promise<GameSession> {
    const response = await apiClient.put<GameSession>(
      API_ENDPOINTS.GAMES.BY_ID(id),
      data
    );
    return response.data;
  }

  /**
   * Export game sessions
   */
  async exportGameSessions(params?: GameSessionsParams): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(
      `${API_ENDPOINTS.GAMES.SESSIONS}/export`,
      {
        params,
        responseType: 'blob',
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const gameService = new GameService();
export default gameService;

